import React from "react";
import { useTableHooks } from "../../../atoms/table/useTableHooks";
import { usePaginationHook } from "../../../molecules/pagination/usePaginationHook";
import { Breadcrumb } from "../../../atoms/breadcrumb";
import { Input } from "../../../atoms/input";
import { Table } from "../../../atoms/table";
import { Divider } from "../../../atoms/divider";
import { Dropdown } from "../../../atoms/dropdown";
import { Pagination } from "../../../molecules/pagination";
import { useOrganizationsPricingQuery } from "../../../../store/pricing/pricingSlice";
import { Icon } from "../../../atoms/icon";
import { numberFormat, numberFormatWithDynamicDecimal } from "../../../../utils";
import { PAGE_NAME, ROUTE, SAAS_TOOLS } from "../../../../constants/constants";

const OrganizationsPricingList = () => {
    const pageClassName = "p-organizationsPricingList";

    const {
        search,
        query,
        pageChangeHandler,
        limitChangeHandler,
        searchHandler,
    } = useTableHooks();


    const { data, isLoading, isFetching, isSuccess } =
        useOrganizationsPricingQuery({
            search: query.search,
            limit: query.limit,
            current_page: query.current,
        });

    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: false,
        },
        {
            title: "Name",
            dataIndex: "name",
            isSortable: false,
        },
        {
            title: "State",
            dataIndex: "district",
            isSortable: false,
        },
        {
            title: "Country",
            dataIndex: "country",
            isSortable: false,
        },
        {
            title: "No of Users",
            dataIndex: "numberOfOrgMember",
        },
        {
            title: "Total",
            dataIndex: "organizationsTotalPrice",
            isSortable: false,
        },
        {
            title: "Okta",
            dataIndex: "oktaCost",
            isSortable: false,
        },

        {
            title: "YouTrack",
            dataIndex: "youtrackCost",
            isSortable: false,
        },
        {
            title: "Box",
            dataIndex: "boxCost",
        },
        {
            title: "Confluence",
            dataIndex: "confluenceCost",
            isSortable: false,
        },
        {
            title: "Atlassian",
            dataIndex: "atlassianCost",
            isSortable: false,
        },
        {
            title: "Gitlab",
            dataIndex: "gitlabCost",
            isSortable: false,
        },
        {
            title: "Miro",
            dataIndex: "miroCost",
            isSortable: false,
        },
    ];

    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    const calculateTotalPrice = (response: { [key: string]: string | number }[], type: string) => {
        let total = 0;
        if (isSuccess) {
            response &&
                response.forEach((item: { [key: string]: string | number }) => {
                    Object.keys(item).forEach((key: string) => {
                        if (SAAS_TOOLS.includes(key)) {
                            total += item[key] as number;
                        }
                    });
                });
        }
        if (type === "Monthly")
            return total;
        else
            return total * 12;
    };


    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <div className={`${pageClassName}__card`}>
                <div className={`${pageClassName}__card__item`}>
                    <div className={`${pageClassName}__card__container`}>
                        <p className={`${pageClassName}__card__title`}>
                            Total Monthly Cost
                        </p>
                        <p className={`${pageClassName}__card__value`}>
                            {isSuccess
                                ? numberFormat(
                                    calculateTotalPrice(data?.result?.data, "Monthly"),"JPY"
                                )
                                : 0}
                        </p>
                    </div>
                    <Icon icon="money" size="tertiary" />
                </div>
                <div className={`${pageClassName}__card__item`}>
                    <div className={`${pageClassName}__card__container`}>
                        <p className={`${pageClassName}__card__title`}>Total Yearly Cost</p>
                        <p className={`${pageClassName}__card__value`}>
                            {isSuccess
                                ? numberFormat(
                                    calculateTotalPrice(data?.result?.data, "Yearly"),"JPY"
                                )
                                : 0}
                        </p>
                    </div>
                    <Icon icon="moneyBundle" size="tertiary" />
                </div>
                <div className={`${pageClassName}__card__item`}>
                    <div className={`${pageClassName}__card__container`}>
                        <p className={`${pageClassName}__card__title`}>
                            Number of Organizations
                        </p>
                        <p className={`${pageClassName}__card__value`}>
                            {isSuccess ? data?.result?.total : 0}
                        </p>
                    </div>
                    <Icon icon="organization" size="tertiary" />
                </div>
                <div className={`${pageClassName}__card__item`}>
                    <div className={`${pageClassName}__card__container`}>
                        <p className={`${pageClassName}__card__title`}>
                            Number of Licenses
                        </p>
                        <p className={`${pageClassName}__card__value`}>
                            {isSuccess
                                ? numberFormatWithDynamicDecimal(data?.result?.totalLicense,0)
                                : 0}
                        </p>
                    </div>
                    <Icon icon="license" size="tertiary" />
                </div>
            </div>

            <p className={`${pageClassName}__title`}>{PAGE_NAME.ORGANIZATIONS_WISE_BUDGET}</p>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={searchHandler}
                            value={search}
                        />
                    </div>
                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading || isFetching}
                        firstLoad={isLoading}
                        minWidth="1600"
                        startIndex={data?.result?.from}
                        viewURL={ROUTE.ORGANIZATION_LIST}
                        cursorPointer
                    />
                </div>
                <Divider />
                <div className={`${pageClassName}__pagination`}>
                    <div className={`${pageClassName}__pagination__limit`}>
                        <p>Display</p>
                        <div className={`${pageClassName}__pagination__dropdown`}>
                            <Dropdown
                                options={[
                                    { label: "20", value: 20 },
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                ]}
                                selected={{ label: query.limit, value: query.limit }}
                                onClickChange={(e) => {
                                    limitChangeHandler(e.value as number);
                                    handleFirstPage();
                                }}
                                isBordered
                                isIcon
                                 
                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__pagination__number`}>
                        {data?.result?.total ? (
                            <p>
                                Show {data?.result?.from} -{" "}
                                {data?.result?.from + query.limit - 1 < data?.result?.total
                                    ? data?.result?.from + query.limit - 1
                                    : data?.result?.total}{" "}
                                of {data?.result?.total}
                            </p>
                        ) : (
                            <p>Show 00 - 00 of 00</p>
                        )}
                    </div>

                    <div className={`${pageClassName}__pagination__nav`}>
                        {data?.result?.total > query.limit && (
                            <Pagination
                                totalData={totalData}
                                page={query.current}
                                handleFirstPage={handleFirstPage}
                                handlePrevPage={handlePrevPage}
                                handleLastPage={handleLastPage}
                                handleNextPage={handleNextPage}
                                renderPageNumbers={renderPageNumbers}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrganizationsPricingList;
